import React, { useState, useRef, Fragment } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BreadCrumbs from '../components/page/BreadCrumbs';
import HtmlHelmet from '../components/common/HtmlHelmet';
import { axiosNewInstance } from '../config/Api';
import toast from 'react-hot-toast';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

/* ─── Password strength checklist (reused from RegisterNew) ─── */
const PasswordChecklist = ({ password }) => {
    const rules = [
        { label: 'At least 10 characters', test: password.length >= 10 },
        { label: 'At least one uppercase letter (A-Z)', test: /[A-Z]/.test(password) },
        { label: 'At least one lowercase letter (a-z)', test: /[a-z]/.test(password) },
        { label: 'At least one number (0-9)', test: /\d/.test(password) },
        { label: 'At least one special character (!@#$%^&*)', test: /[!@#$%^&*]/.test(password) },
    ];

    return (
        <ul style={{ listStyle: 'none', padding: 0, marginTop: '8px', fontSize: '0.85rem' }}>
            {rules.map((rule, i) => (
                <li key={i} style={{ color: rule.test ? '#28a745' : '#dc3545', marginBottom: '2px' }}>
                    {rule.test ? '✅' : '❌'} {rule.label}
                </li>
            ))}
        </ul>
    );
};

const ForgotPasswordNewForm = () => {
    const history = useHistory();
    const location = useLocation();

    // Context passed from Login components
    const locationState = location.state || {};
    const prefillIdentifier = locationState.mobile || '';
    const portalType = locationState.login_type || 'office'; // Default to office if reached directly

    // Steps: 'identify' | 'otp' | 'password'
    const [step, setStep] = useState('identify');
    const [userIdentifier, setUserIdentifier] = useState(prefillIdentifier);
    const [actualMobile, setActualMobile] = useState('');
    const [maskedMobile, setMaskedMobile] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [loading, setLoading] = useState(false);

    // OTP
    const [otpDigits, setOtpDigits] = useState(['', '', '', '']);
    const ref0 = useRef(null);
    const ref1 = useRef(null);
    const ref2 = useRef(null);
    const ref3 = useRef(null);
    const inputRefs = [ref0, ref1, ref2, ref3];
    const [verifiedOtp, setVerifiedOtp] = useState('');

    // Password
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newDigits = [...otpDigits];
        newDigits[index] = value.slice(-1);
        setOtpDigits(newDigits);
        if (value && index < 3) inputRefs[index + 1].current?.focus();
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    /* ─── Step 1: Look up user → Send OTP to their mobile ─── */
    const handleSendOtp = async (e) => {
        e.preventDefault();

        if (!userIdentifier) {
            setMessage('Username / Email / Mobile is required.');
            setMessageType('danger');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            // 1. Look up user by any identifier to get their actual mobile
            const initResp = await axiosNewInstance.post('/api-app/forgot-password-init', {
                user_id: userIdentifier,
                login_type: portalType,
            });

            if (initResp.data?.status !== 1) {
                setMessage(initResp.data?.message || 'User not found.');
                setMessageType('danger');
                setLoading(false);
                return;
            }

            const resolvedMobile = initResp.data.mobile;
            const resolvedMasked = initResp.data.masked_mobile;
            setActualMobile(resolvedMobile);
            setMaskedMobile(resolvedMasked);

            // 2. Send OTP to the user's actual mobile
            const response = await axiosNewInstance.post('/api/send-mobile-otp', {
                phone: resolvedMobile,
            });

            if (response.data && (response.data.response === 'sms-sent' || response.data === 'sms-sent')) {
                setMessage(`OTP sent to ${resolvedMasked}`);
                setMessageType('success');
                setStep('otp');
                setOtpDigits(['', '', '', '']);
            } else if (response.data && response.data.response === 'sms-limit-exceeded') {
                setMessage('SMS limit exceeded for today. Please try again later.');
                setMessageType('danger');
            } else {
                setMessage('Failed to send OTP. Please try again.');
                setMessageType('danger');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setMessage('User not found.');
            } else {
                console.error('Error:', error);
                setMessage('Failed to send OTP. Please try again.');
            }
            setMessageType('danger');
        } finally {
            setLoading(false);
        }
    };

    /* ─── Step 2: Verify OTP ─── */
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otp = otpDigits.join('');
        if (otp.length !== 4) {
            setMessage('Please enter the 4-digit OTP.');
            setMessageType('danger');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await axiosNewInstance.post('/api/validate-mobile-otp', {
                phone: actualMobile,
                otp: otp,
            });

            const data = response.data;
            const result = typeof data === 'string' ? data : data?.response || data;

            if (result === 'right') {
                setVerifiedOtp(otp);
                setStep('password');
                setMessage('OTP verified! Please set your new password.');
                setMessageType('success');
            } else {
                setMessage('Invalid OTP. Please try again.');
                setMessageType('danger');
            }
        } catch (error) {
            console.error('OTP verification error:', error);
            setMessage('OTP verification failed. Please try again.');
            setMessageType('danger');
        } finally {
            setLoading(false);
        }
    };

    /* ─── Step 3: Reset Password ─── */
    const handleResetPassword = async (e) => {
        e.preventDefault();

        // Client-side validation
        const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{10,}$/;
        if (!strongPasswordRegex.test(password)) {
            setMessage('Password does not meet the strength requirements.');
            setMessageType('danger');
            return;
        }

        if (password !== confirmPassword) {
            setMessage('Passwords do not match.');
            setMessageType('danger');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await axiosNewInstance.post('/api-app/forgot-password-new', {
                mobile: actualMobile,
                otp: verifiedOtp,
                password: password,
                password_confirmation: confirmPassword,
            });

            if (response.data?.status === 1) {
                toast.success(response.data.message || 'Password changed successfully!');
                if (portalType === 'office') {
                    history.push('/login-office');
                } else if (portalType === 'employee') {
                    history.push('/login-employee');
                } else {
                    history.push('/login-customer');
                }
            } else {
                setMessage(response.data?.message || 'Password reset failed.');
                setMessageType('danger');
            }
        } catch (error) {
            const errMsg = error.response?.data?.message;
            if (typeof errMsg === 'object') {
                const firstError = Object.values(errMsg)[0];
                setMessage(Array.isArray(firstError) ? firstError[0] : JSON.stringify(errMsg));
            } else {
                setMessage(errMsg || 'Password reset failed. Please try again.');
            }
            setMessageType('danger');
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="sign-in">
            <div className="container">
                <div className="form-title mb-4">
                    <h3 className="text-center">Forgot Password</h3>
                </div>

                <div className="sign-in-form" style={{ maxWidth: '500px', margin: '0 auto 40px' }}>
                    <div className="row">
                        {message && (
                            <div className="col-sm-12">
                                <div className={`alert alert-${messageType} alert-dismissible fade show`} role="alert">
                                    {message}
                                    <button type="button" className="close" onClick={() => setMessage('')}>
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ─── Step 1: Identifier + Send OTP ─── */}
                    {step === 'identify' && (
                        <form onSubmit={handleSendOtp}>
                            <label style={{ fontWeight: '500', marginBottom: '5px', display: 'block' }}>Username / Email / Mobile</label>
                            <input
                                type="text"
                                className="form-control"
                                value={userIdentifier}
                                onChange={(e) => setUserIdentifier(e.target.value)}
                                readOnly={!!prefillIdentifier}
                                style={{
                                    marginBottom: '15px',
                                    backgroundColor: prefillIdentifier ? '#e9ecef' : '',
                                }}
                                placeholder="Enter your Username / Email / Mobile"
                                autoFocus
                            />
                            <button
                                type="submit"
                                className="btn btn-primary btn-block"
                                disabled={loading}
                                style={{ marginTop: '10px' }}
                            >
                                {loading ? 'Sending OTP...' : 'Send OTP'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-link btn-block"
                                onClick={() => history.push(portalType === 'office' ? '/login-office' : '/login-customer')}
                                style={{ marginTop: '5px' }}
                            >
                                ← Back to Login
                            </button>
                        </form>
                    )}

                    {/* ─── Step 2: OTP Verification ─── */}
                    {step === 'otp' && (
                        <form onSubmit={handleVerifyOtp}>
                            <p style={{ marginBottom: '15px', color: '#666', textAlign: 'center' }}>
                                Enter the 4-digit OTP sent to {maskedMobile}
                            </p>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }}>
                                {[0, 1, 2, 3].map((index) => (
                                    <input
                                        key={index}
                                        ref={inputRefs[index]}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        className="form-control"
                                        style={{ width: '55px', textAlign: 'center', fontSize: '1.5rem' }}
                                        value={otpDigits[index]}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    />
                                ))}
                            </div>
                            <button
                                type="submit"
                                className="btn btn-primary btn-block"
                                style={{ marginTop: '10px' }}
                            >
                                Verify OTP
                            </button>
                            <button
                                type="button"
                                className="btn btn-link btn-block"
                                onClick={() => { setStep('identify'); setMessage(''); setOtpDigits(['', '', '', '']); }}
                                style={{ marginTop: '10px' }}
                            >
                                ← Back
                            </button>
                        </form>
                    )}

                    {/* ─── Step 3: New Password ─── */}
                    {step === 'password' && (
                        <form onSubmit={handleResetPassword}>
                            <label style={{ fontWeight: '500', marginBottom: '5px', display: 'block' }}>New Password</label>
                            <div style={{ position: 'relative', marginBottom: '5px' }}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="New Password"
                                    className="form-control"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoFocus
                                />
                                <span
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute', right: '12px', top: '50%',
                                        transform: 'translateY(-50%)', cursor: 'pointer', color: '#888',
                                    }}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>

                            <PasswordChecklist password={password} />

                            <label style={{ fontWeight: '500', marginBottom: '5px', marginTop: '15px', display: 'block' }}>Confirm Password</label>
                            <div style={{ position: 'relative', marginBottom: '15px' }}>
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Confirm Password"
                                    className="form-control"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                                <span
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    style={{
                                        position: 'absolute', right: '12px', top: '50%',
                                        transform: 'translateY(-50%)', cursor: 'pointer', color: '#888',
                                    }}
                                >
                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>
                            {confirmPassword && password !== confirmPassword && (
                                <p style={{ color: '#dc3545', fontSize: '0.85rem' }}>❌ Passwords do not match</p>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary btn-block"
                                disabled={loading}
                                style={{ marginTop: '15px' }}
                            >
                                {loading ? 'Resetting Password...' : 'Reset Password'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

const ForgotPasswordNew = () => {
    const meta_data = {
        meta_title: 'Forgot Password',
        meta_description: 'Reset your password',
        meta_keywords: 'forgot, password, reset, lic',
    };

    const bread_cumbs = [
        {
            bn_title: "পাসওয়ার্ড ভুলে গেছেন",
            title: "Forgot Password",
            url: "/forgot-pass-new",
        },
        {
            bn_title: "হোম",
            title: "Home",
            url: "/",
        },
    ];

    return (
        <Fragment>
            <HtmlHelmet custom_meta={meta_data} />
            <BreadCrumbs bread_curmbs={bread_cumbs} />
            <ForgotPasswordNewForm />
        </Fragment>
    );
};

export default ForgotPasswordNew;
